/**
 * Contracts API Types
 * Zod schemas for API response validation
 */

import { z } from '@ufc/core';

// ============================================
// Decimal Preprocessing
// ============================================

const decimalSchema = z.preprocess((value) => {
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : value;
  }
  return value;
}, z.number().finite());

// ============================================
// API Response Schemas
// ============================================

export const apiSalesOrderLineSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  committedQuantity: decimalSchema,
  actualQuantity: decimalSchema.optional(),
  canceledQuantity: decimalSchema.optional(),
  effectiveCommittedQuantity: decimalSchema.optional(),
  remainingQuantity: decimalSchema.optional(),
  variance: decimalSchema.optional(),
  fulfillmentProgress: decimalSchema.optional(),
  splitBy: decimalSchema,
  status: z.enum(['active', 'on_hold', 'completed', 'cancelled']).optional(),
  metadata: z.any().optional(),
  variant: z.any().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// Tenant reference (populated for admin views)
const tenantRefSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    documentId: z.string().optional(),
    name: z.string(),
    code: z.string(),
  })
  .optional()
  .nullable();

// Factory reference (populated for factory display)
const factoryRefSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    documentId: z.string().optional(),
    name: z.string().optional(),
    code: z.string().optional(),
  })
  .optional()
  .nullable();

// City reference (populated for city-first destination)
const cityRefSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    documentId: z.string().optional(),
    name: z
      .union([
        z.string(),
        z.object({ en: z.string().optional(), ar: z.string().optional() }),
      ])
      .optional(),
  })
  .optional()
  .nullable();

// Order reference for count (populated from reverse relation)
const orderRefSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  orderStatus: z.string().optional(),
});

export const apiSalesOrderSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  salesOrderNumber: z.string().optional(),
  validityEnd: z.string().nullable().optional(),
  status: z.enum([
    'draft',
    'active',
    'on_hold',
    'canceled',
    'completed',
    'failed',
  ]),
  metadata: z.any().optional(),
  tenant: tenantRefSchema,
  factory: factoryRefSchema,
  city: cityRefSchema,
  salesOrderLines: z.array(apiSalesOrderLineSchema).optional().default([]),
  orders: z.array(orderRefSchema).optional().default([]),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  publishedAt: z.string().optional(),
  createdByUser: z
    .object({
      id: z.number(),
      username: z.string().optional().nullable(),
      email: z.string().optional().nullable(),
      fullName: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
});

export const salesOrdersResponseSchema = z.object({
  data: z.array(apiSalesOrderSchema),
  meta: z
    .object({
      pagination: z.object({
        page: z.number(),
        pageSize: z.number(),
        pageCount: z.number(),
        total: z.number(),
      }),
    })
    .optional(),
});

export const salesOrderResponseSchema = z.object({
  data: apiSalesOrderSchema,
  meta: z.object({}).optional(),
});

export const salesOrderStatsDtoSchema = z.object({
  active: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  draft: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  totalValue: z.object({
    amount: z.number().nonnegative(),
    currency: z.string().min(1),
  }),
});

export const salesOrderStatsResponseSchema = z.object({
  data: salesOrderStatsDtoSchema,
});

// ============================================
// Inferred Types
// ============================================

export type ApiSalesOrderDto = z.infer<typeof apiSalesOrderSchema>;
export type ApiSalesOrderLineDto = z.infer<typeof apiSalesOrderLineSchema>;
export type SalesOrdersApiResponse = z.infer<typeof salesOrdersResponseSchema>;
export type SalesOrderApiResponse = z.infer<typeof salesOrderResponseSchema>;
export type SalesOrderStatsDto = z.infer<typeof salesOrderStatsDtoSchema>;
export type SalesOrderStatsApiResponse = z.infer<
  typeof salesOrderStatsResponseSchema
>;

// ============================================
// Legacy Aliases
// ============================================

export type StrapiSalesOrderDto = ApiSalesOrderDto;
export type StrapiSalesOrderLineDto = ApiSalesOrderLineDto;
